import { useEffect, useState, useContext } from 'react';
import { Link as RouterLink } from 'react-router-dom';
// material
import {
  Grid,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Box,
  Stack,
  Container,
  Typography,
  Link,
  Alert,
  Chip,
  Fab,
  FormControlLabel,
  Switch,
  ButtonBase,
  Button
} from '@mui/material';
import { grey } from '@mui/material/colors';
import { styled } from '@mui/material/styles';
import { Icon } from '@iconify/react';
import externalLinkFill from '@iconify/icons-eva/external-link-fill';
import plusFill from '@iconify/icons-eva/plus-fill';
// contexts
import { useAuth } from '../../contexts/Auth';
import { SettingsContext } from '../../contexts/Settings';
// helpers
import { getEventDomain } from '../../helpers/Util';
// services
import { listEvents, activateEvent, inactivateEvent } from '../../services/Event';
// components
import Page from '../../components/Page';
import Loader from '../../components/Loader';

const CardStyleHover = styled(Card)(({ theme }) => ({
  boxShadow: 'none',
  border: `1px solid ${grey[300]}`,
  '&:hover': {
    boxShadow: theme.customShadows.z24
  }
}));

const CardStyle = styled(Card)(({ theme }) => ({
  boxShadow: 'none',
  marginBottom: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    border: `1px dashed ${grey[300]}`
  }
}));

export default function Event() {
  const { authUser } = useAuth();
  const { authSettings } = useContext(SettingsContext);

  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const authUserId = authUser.uid;
        const events = await listEvents(authUserId);

        setEvents(events);
      } catch (err) {
        setHasError(true);
        console.error(err);
      }

      setIsLoading(false);
    };

    fetchData();
  }, [authUser]);

  const getDate = (event) => {
    switch (event.calendar.recurrence.frequency) {
      case 'daily':
        return 'Daily';
      case 'weekly':
        return 'Weekly';
      case 'no-repeat':
        return 'Only Once';
      default:
        return 'No date';
    }
  };

  const handleStatus = async (event) => {
    try {
      const idToken = await authUser.getIdToken();
      const headers = {
        Authorization: `Bearer ${idToken}`,
        'Account-Key': authSettings.server.path
      };

      if (event.active) {
        await inactivateEvent({ headers, id: event._id });
      } else {
        await activateEvent({ headers, id: event._id });
      }

      setEvents((currentEvents) =>
        currentEvents.map((currentEvent) => {
          if (currentEvent._id === event._id) {
            return {
              ...currentEvent,
              active: !event.active
            };
          }
          return currentEvent;
        })
      );
    } catch (err) {
      alert('An error occurred — Please, try again!');
      console.error(err);
    }
  };

  if (isLoading && !hasError) {
    return <Loader align="top" />;
  }

  if (hasError) {
    return (
      <Alert severity="error">
        An error occurred while trying to load the page — Please, try to refresh!
      </Alert>
    );
  }

  return (
    <Page title="Event" path="/event">
      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            My Events
            <Typography component="span" variant="body2" color="grey.500" sx={{ display: 'block' }}>
              Select an event to view details/edit
            </Typography>
          </Typography>
          <Fab
            size="medium"
            color="primary"
            aria-label="add"
            sx={{ flexShrink: 0 }}
            to="/event/create"
            component={RouterLink}
          >
            <Icon icon={plusFill} width="24" />
          </Fab>
        </Stack>

        {events.length ? (
          <Grid container spacing={2}>
            {events.map((event) => (
              <Grid key={event._id} item xs={12} sm={6} md={4}>
                <CardStyleHover>
                  <ButtonBase
                    disableRipple
                    href={`/dashboard/sales?eventId=${event._id}`}
                    sx={{
                      display: 'block'
                    }}
                  >
                    <CardHeader
                      title={
                        <Typography variant="h6" noWrap sx={{ width: '200px' }}>
                          {event.name}
                        </Typography>
                      }
                      subheader={getDate(event)}
                    />
                    <CardContent>
                      <Box
                        component="img"
                        src={event.theme?.logo?.regular?.url}
                        sx={{
                          margin: '0 auto',
                          my: 3,
                          width: 'auto',
                          height: 50
                        }}
                      />
                    </CardContent>
                  </ButtonBase>
                  <CardActions>
                    <Button
                      startIcon={<Icon icon={externalLinkFill} width="24" />}
                      href={`//${event.route?.subdomain}.${getEventDomain()}`}
                      target="_blank"
                      component={Link}
                      disabled={!(event.active && event.route?.subdomain)}
                    >
                      Go Live
                    </Button>
                    <FormControlLabel
                      checked={event.active}
                      control={<Switch onChange={() => handleStatus(event)} />}
                      sx={{ color: 'black', ml: 'auto', mr: 0 }}
                    />
                    <Chip
                      label={event.active ? 'active' : 'inactive'}
                      color={event.active ? 'success' : 'error'}
                      size="small"
                      sx={{ marginLeft: '0 !important' }}
                    />
                  </CardActions>
                </CardStyleHover>
              </Grid>
            ))}
          </Grid>
        ) : (
          <CardStyle>
            <Box sx={{ py: 5, textAlign: 'center' }}>
              <Typography variant="h5" sx={{ mb: 2 }}>
                No events registered!
                <Typography
                  component="span"
                  variant="body2"
                  color="grey.500"
                  sx={{ display: 'block', maxWidth: '300px', mx: 'auto' }}
                >
                  Create your first event!
                </Typography>
              </Typography>

              <Stack
                spacing={{ xs: 1, sm: 2 }}
                direction={{ xs: 'column', sm: 'row' }}
                alignItems="center"
                justifyContent="center"
              >
                <Button variant="contained" size="large" to="/event/create" component={RouterLink}>
                  Create Event
                </Button>
              </Stack>
            </Box>
          </CardStyle>
        )}
      </Container>
    </Page>
  );
}
